/*********************************************************
 * Copyright (C) 2015-2017 VMware, Inc. All rights reserved.
 *********************************************************/
/**
 * @fileoverview resourcemanager.js -- resourceManager
 * Service to handle resources(mainly audio-in and video-in devices) management
 *
 * currently we only allow devices to be used by one of the wmks session, and before it
 * releases the resources, other sesion can not get the resouce.
 */

import RTAV from './logservice';

let resourceManager = (function() {
   let activeSessionID = null,
      resourcePool = null,
      // private function
      initResourcesPool,
      deviceStatusChangeCallback;

   initResourcesPool = function(onDone) {
      let mediaSrc;
      if (!resourcePool) {
         resourcePool = {
            src: new RTAV.MediaCapture(),
            mediaEnc: new RTAV.MediaEncoder()
         };
      }
      onDone(resourcePool);
   };

   return {
      /**
       * Used to check whether there is any usable devices
       * cuurrently we only allow device to be used by one desktop at a time.
       * @return {Boolean} This returns whether there is any usable devices
       */
      hasUsableResources: function() {
         return activeSessionID === null;
      },

      /**
       * Used to check whether a session have usable devices, and if not we should not
       * process the RTAV function on that session.
       * currently we only have one session can work, so the condition is very simple.
       * @param {string}  sessionID The ID of the session that will be checked.
       * @return {Boolean} This returns whether the device has already been occupied
       */
      hasOccupiedResources: function(sessionID) {
         return activeSessionID === sessionID;
      },

      /**
       * Used to occupy device for specified session.
       * Should only be call this API when hasOccupiedResources returns false, and hasUsableResources returns true.
       * @param {string} sessionID The ID of session that wants to occupy the devices
       * @param {function} onDone The callback to pass resources out, null means fail to occupy
       */
      occupyResources: function(sessionID, onDone) {
         if (this.hasOccupiedResources(sessionID)) {
            RTAV.logService.devError('session "' + sessionID + '" has already occupy a device');
            onDone(null);
            return;
         }
         if (!this.hasUsableResources()) {
            RTAV.logService.devError('there is no rest device for session "' + sessionID + '"');
            onDone(null);
            return;
         }
         activeSessionID = sessionID;

         this.emitDeviceStatusChanged();

         initResourcesPool(onDone);
      },

      /**
       * Used to release device for specified session.
       * Should only be call this API when hasOccupiedResources returns true.
       * @param {string} sessionID The ID of session that wants to release the devices
       * @param {bool} forcibly Whether bypass the owner ship check to release owner's devices
       * @param {function} onDone The callback to pass resources out, null means fail to occupy
       */
      releaseResources: function(sessionID, forcibly) {
         if (!forcibly && !this.hasOccupiedResources(sessionID)) {
            RTAV.logService.error('session "' + sessionID + '" hasn\'t occupied a device');
            return;
         }
         activeSessionID = null;
      },

      /**
       * Set the callback function to detect the device status changes
       * @param {function} callback The callback function passed from the UI layer
       */
      onDeviceStatusChanged: function(callback) {
         deviceStatusChangeCallback = callback;
      },

      /**
       * Trigger DeviceStatusChanged function from outside
       */
      emitDeviceStatusChanged: function() {
         if (typeof deviceStatusChangeCallback === 'function') {
            deviceStatusChangeCallback();
         }
      },

      /**
       * @return {string} This returns the current device using id, if not in use, return null
       */
      getWorkingSessionId: function() {
         return activeSessionID;
      },

      /**
       * Let a new session steal device from current using session
       * @param  {string} sessionID The new session ID
       * @param  {function} onDone  The callback function once the device hand over is finished
       */
      stealSession: function(sessionID, onDone) {
         if (!sessionID || typeof onDone !== 'function') {
            return;
         }
         RTAV.wmksAdapter.userStopSession(activeSessionID);
         this.occupyResources(sessionID, onDone);
      },

      /**
       * Close the devices for RTAV, later usage must require another premission
       * @param  {string} sessionID The ID of session from which this function is called
       * since we close device when not using them, there is no need to close them manully anymore
       */
      closeDevices: function(sessionID) {
         if (!sessionID || !this.hasOccupiedResources(sessionID)) {
            return;
         }
         RTAV.wmksAdapter.userStopSession(sessionID);
      }
   };
}());

export default resourceManager;